<script setup>
import { RichTextRenderer } from '@marvr/storyblok-rich-text-vue-renderer'

const props = defineProps({ blok: Object, isVisible: Boolean, reverse: Boolean, darkmode: Boolean })
const emits = defineEmits(['getSize'])
const target = ref(null)
const { height, width } = useElementSize(target)

const textClasses = computed(() => {
  switch (props.blok.text_size) {
    case 'sustainable':
      return 'text-xl !font-600 border-2 border-green rounded-tl-8 rounded-br-8 px-6 py-4 !text-green-600 '
    case 'lg':
      return 'text-3xl md:text-4xl xl:text-5xl'
    case 'xl':
      return 'text-5xl md:text-6xl xl:text-7xl'
    default :
      return 'text-3xl md:text-4xl xl:text-5xl'
  }
})

const getWidth = ref(0)
const getHeight = ref(0)

function onResize() {
  getWidth.value = width.value
  getHeight.value = height.value
  emits('getSize', width.value, height.value)
}

onMounted(() => {
  onResize()
  window.addEventListener('resize', onResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
})
</script>

<template>
  <div
    v-if="blok.component === 'model'" id="parent"
    :class="reverse ? 'right-0' : 'left-0'"
    class="inset-y-0 z-50 mb-custom-xl h-[300px] w-full flex items-center 3xl:relative lg:mb-0 lg:h-[500px] sm:h-[400px] lg:flex-1"
  >
    <LazyScene
      ref="target"
      :scale="1.1"
      :pos-y="0"
      :ro-y="0"
      :models="[
        {
          model: `https://cdn.jsdelivr.net/gh/webmaster-dexontechnology/gh-cdn/model/${blok.model}`,
        },
      ]"
      :class="[!isVisible && reverse ? '!translate-x-[25%]' : '', isVisible ? 'opacity-100 translate-x-[0%]' : 'opacity-0 translate-x-[-25%]']"
      class="transition-[transform,opacity] h-full w-full flex items-center justify-center duration-700 delay-150 ease-in-out"
    />
  </div>
  <div
    v-if="blok.component === 'image'"

    :class="[!isVisible && reverse ? '!translate-x-[25%]' : '', reverse ? 'order-2' : '', isVisible ? 'opacity-100 translate-x-[0%]' : 'opacity-0 translate-x-[-25%]']" class="transition-[transform,opacity] inset-y-0 flex items-center duration-700 delay-150 ease-in-out lg:mb-0 lg:flex-1"
  >
    <UIImage
      ref="target"
      :alt="blok.media.alt"
      :filename="`${blok.media.filename}/m/860x573/filters:quality(80)`"
      class="z-0 object-cover"
      classes="rounded-3 lg:rounded-4"
    />
  </div>
  <!-- eslint-disable -->
  <div 
    v-if="blok.component === 'columns'" 
    :class="[!isVisible && reverse ? '!translate-x-[-25%]' : '', reverse ? 'lg:mr-auto' : 'lg:ml-auto', isVisible ? 'opacity-100 translate-x-[0%]' : 'opacity-0 translate-x-[25%]']" class="transition-[transform,opacity] w-full duration-700 delay-300 ease-in-out lg:flex-1"
  >
    <div 
      v-if="blok.overlap" :class="reverse ? 'translate-x-[-40%] left-0' : 'translate-x-[40%] right-0'" 
      class="absolute top-0 translate-y-[-47.5%] text-[12rem] font-bold uppercase text-blue-800/10 lg:text-[15rem] md:text-[16rem]"
    >
      {{ blok.header }}
    </div>
    <div
      v-else-if="blok.header && !blok.overlap"
      class="mb-1 text-md font-bold capitalize text-secondary-red-100 lg:text-xl md:text-xl sm:text-lg"
    >
      {{ blok.header }}
    </div>
    <h2 class="mx-auto mb-custom-md font-bold leading-[1.2]" :class="[darkmode ? 'text-white' : 'text-primary', textClasses]">
      {{ blok.title }}
    </h2>
    <div v-if="blok.content" class="columns-content"  :class="{ 'columns-content-dark': darkmode }">
      <RichTextRenderer :document="blok.content" />
    </div>
    <ul v-if="blok.actions.length" class="mt-custom-xl flex flex-wrap gap-4 gap-y-custom-md p-0">
      <li v-for="action in blok.actions" :key="action.uuid" class="mb-0">
        <StoryblokComponent :blok="action" />
      </li>
    </ul>
  </div>
</template>
